import {
  FeatureAppManager,
  FeatureServiceConsumerDefinition,
  FeatureServiceRegistry,
  Logger,
} from '@feature-hub/core';

import {
  INTEGRATOR_ID,
  LoggingExternalsValidator,
} from '@oneaudi/integrator-shared';

import {
  IntegratorFeatureServices,
  featureServiceDependencies,
} from './service-dependencies';

import { defineExternals, loadAmdModule } from '@feature-hub/module-loader-amd';
import { definedExternals, providedExternals } from './provided-externals';

import { ContentStore } from '@oneaudi/content-service';
import { activateHistoryServiceForNavigation } from '../feature-services/feature-service-history';
import { createFeatureServiceDefinitions } from './service-definitions';
import { createLogger } from '../create-logger';
import { processAuthService } from '../feature-services/feature-service-auth';
import { processSerializedStates } from '../feature-services/feature-service-serialized-states';

export const setupIntegrator = (): {
  contentStore: ContentStore;
  featureAppManager: FeatureAppManager;
  featureServices: IntegratorFeatureServices;
  logger: Logger;
} => {
  defineExternals(definedExternals);

  const logger = createLogger();

  const externalsValidator = new LoggingExternalsValidator(
    providedExternals,
    logger,
  );

  const featureServiceRegistry = new FeatureServiceRegistry({
    externalsValidator,
    logger,
  });

  const integratorDefinition: FeatureServiceConsumerDefinition = {
    dependencies: { featureServices: featureServiceDependencies },
  };

  const { featureServiceDefinitions: featureServiceDefinitions, contentStore } =
    createFeatureServiceDefinitions(logger);

  featureServiceRegistry.registerFeatureServices(
    featureServiceDefinitions,
    INTEGRATOR_ID,
  );

  const featureAppManager = new FeatureAppManager(featureServiceRegistry, {
    externalsValidator,
    logger,
    moduleLoader: loadAmdModule,
  });

  const { featureServices } = featureServiceRegistry.bindFeatureServices(
    integratorDefinition,
    INTEGRATOR_ID,
  );

  (window.audiIntegrator ||= {}).printFeatureHubInfo = (): void => {
    /* eslint-disable no-console */
    console.group('Feature Services');
    console.table(
      featureServiceRegistry
        .getInfo()
        .featureServices.map(({ id, versions }) => ({
          id,
          versions: versions.join(', '),
        })),
    );
    console.groupEnd();

    console.group('Externals');
    console.table(
      Object.entries(providedExternals).map(([name, version]) => ({
        name,
        version,
      })),
    );
    console.groupEnd();
    /* eslint-enable no-console */
  };

  return {
    contentStore,
    featureAppManager,
    featureServices: featureServices as unknown as IntegratorFeatureServices,
    logger,
  };
};

export const processFeatureServices = (
  featureServices: IntegratorFeatureServices,
): void => {
  const serializedStateManager = featureServices['s2:serialized-state-manager'];
  processSerializedStates(serializedStateManager);

  const authServiceProvider = featureServices['vw:authService'];
  const authService = authServiceProvider.register('myaudi');
  processAuthService(authService);

  const historyService = featureServices['s2:history'];
  activateHistoryServiceForNavigation(historyService);
};
